import api from "@/utils/api";
import { CreateQuoteDto, Quote, QuoteFilters, SubscriptionTerm, TaxBreakdown, TaxItemDetail, TSubscriptionTerm, UpdateQuoteDto } from "./types";
import { ApiListResponse } from "@/types/api";
import type { TypedPricesChoice } from "@/utils/quoteCurrencyControl";

export const getQuotes = async (filters: QuoteFilters): Promise<{quotes: Quote[]} & ApiListResponse> => {
    return (await api.get('/quotes', { params: filters })).data;
}

export const getQuote = async (id: string): Promise<Quote> => {
    return (await api.get(`/quotes/${id}`)).data;
}

export const createQuote = async (quote: CreateQuoteDto): Promise<Quote> => {
    return (await api.post('/quotes', quote)).data;
}

/**
 * Create a change-order quote (internal only). Seeded with the current line
 * items of its source and edited in place. Provide either:
 *  - sourceOrderId   — restate a single order (per-order entry), or
 *  - parentProposalId — restate the whole proposal's orders (per-proposal entry).
 */
export const createChangeOrderQuote = async ({
    parentProposalId,
    sourceOrderId,
    changeReason,
}: {
    parentProposalId?: string;
    sourceOrderId?: string;
    changeReason?: string;
}): Promise<Quote> => {
    return (await api.post('/quotes/change-order', { parentProposalId, sourceOrderId, changeReason })).data;
}

export type ChangeOrderSummaryLine = {
    productId: string | null;
    name: string;
    action: 'ADDED' | 'REMOVED' | 'CHANGED' | 'UNCHANGED';
    fromQuantity: number;
    toQuantity: number;
    unitPrice: number;
    baselineLineTotal: number;
    currentLineTotal: number;
    lineDelta: number;
};
export type ChangeOrderSummary = {
    baselineSubtotal: number;
    currentSubtotal: number;
    /** Tax collected on the original order(s) for the diffed items. */
    baselineTax: number;
    /** Tax on the restated scope, against the original order's ship-to. */
    currentTax: number;
    /** currentTax − baselineTax; already included in netChange. */
    taxDelta: number;
    /** True when currentTax is a rate-based estimate (live lookup unavailable). */
    taxIsEstimate: boolean;
    /** Net billed (>0) or credited (<0), including the tax movement. */
    netChange: number;
    lines: ChangeOrderSummaryLine[];
} | null;

/** Diff of a change order vs. the original order(s) it amends (null for non-COs). */
export const getChangeOrderSummary = async (quoteId: string): Promise<ChangeOrderSummary> => {
    return (await api.get(`/quotes/${quoteId}/change-order-summary`)).data;
}

export const addProductToQuote = async ({
    quoteId,
    items
}: {
    quoteId: string,
    items: Array<{
        productId?: string;
        quantity: number;
        unitPrice?: number;
        listPrice?: number;
        productType?: string;
        customName?: string;
        customDescription?: string;
    }>
}): Promise<Quote> => {
    return (await api.post(`/quotes/${quoteId}/items`, { items })).data;
}

export const removeProductFromQuote = async ({quoteId, quoteItemId}: {quoteId: string, quoteItemId: string}): Promise<Quote> => {
    return (await api.delete(`/quotes/${quoteId}/items/${quoteItemId}`)).data;
}

export const updateQuoteItemQuantity = async ({quoteId, quoteItemId, quantity}: {quoteId: string, quoteItemId: string, quantity: number}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}/items/${quoteItemId}`, { quantity })).data;
}

export const updateQuoteItemPrice = async ({
    quoteId,
    quoteItemId,
    unitPrice,
    listPrice,
    quantity,
}: {
    quoteId: string,
    quoteItemId: string,
    unitPrice?: number,
    listPrice?: number,
    quantity?: number
}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}/items/${quoteItemId}`, { quantity: quantity || 1, unitPrice, listPrice })).data;
}

export const updateQuoteItemDetails = async ({
    quoteId,
    quoteItemId,
    quantity,
    unitPrice,
    listPrice,
    nameOverride,
    descriptionOverride,
    isOptional,
    isSelected,
}: {
    quoteId: string;
    quoteItemId: string;
    quantity: number;
    unitPrice?: number;
    listPrice?: number;
    nameOverride?: string | null;
    descriptionOverride?: string | null;
    isOptional?: boolean;
    isSelected?: boolean;
}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}/items/${quoteItemId}`, {
        quantity,
        unitPrice,
        listPrice,
        nameOverride,
        descriptionOverride,
        isOptional,
        isSelected,
    })).data;
}

export const updateQuote = async ({quoteId, quote}: {quoteId: string, quote: UpdateQuoteDto}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}`, quote)).data;
}

// delete quote
export const deleteQuote = async (quoteId: string): Promise<void> => {
    await api.delete(`/quotes/${quoteId}`);
}


// Calculate tax for a quote
export const calculateQuoteTax = async ({quoteId, shippingAddressId}: {quoteId: string, shippingAddressId: string}): Promise<{
    taxAmount: number;
    rate: number;
    itemizedTax: TaxItemDetail[];
    breakdown?: TaxBreakdown;
    taxableAmount?: number;
    // Task 3's tax-exemption preview field (tax.controller.ts's calculateQuoteTax),
    // consumed by Task 13's checkoutCertPrompt gate.
    exemptionBasis?: 'CERT' | 'LEGACY_FLAG' | 'NONE' | null;
    /** Status of the certificate behind a CERT basis — only a VERIFIED certificate exempts. */
    certStatus?: 'VERIFIED' | null;
    /** Tax was charged while a certificate for this state is under review (subject to confirmation). */
    certPendingReview?: boolean;
}> => {
    return (await api.post('/taxes/calculate-quote', { quoteId, shippingAddressId })).data;
}

export const updateSubscriptionTerm = async ({quoteId, term}: {quoteId: string, term: SubscriptionTerm}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}/subscription-term`, { term })).data;
}

export const updateRenewalTerm = async ({quoteId, term}: {quoteId: string, term: SubscriptionTerm}): Promise<Quote> => {
    return (await api.put(`/quotes/${quoteId}/renewal-term`, { renewalTerm: term })).data;
}

export const getAvailableSubscriptionTerms = async ({quoteId}: {quoteId: string}): Promise<TSubscriptionTerm[]> => {
    return (await api.get(`/quotes/${quoteId}/available-terms`)).data.availableTerms;
}

export const duplicateQuote = async (quoteId: string, name?: string): Promise<Quote> => {
    return (await api.post(`/quotes/${quoteId}/duplicate`, { name })).data;
}

export const overrideQuoteShipping = async (
    quoteId: string,
    shippingAmount: number,
): Promise<Quote & { warning?: string; carrierMin?: number }> => {
    return (await api.post(`/quotes/${quoteId}/shipping-override`, { shippingAmount })).data;
}

/** Returns whether the logged-in user can override shipping costs on quotes. */
export const canOverrideShipping = async (): Promise<boolean> => {
    try {
        const resp = await api.get('/quotes/can-override-shipping');
        return resp.data?.allowed === true;
    } catch {
        return false;
    }
}

export type ChangeQuoteCurrencyResult = {
    currency: string;
    repricedLines: number;
    /** Typed prices converted at the exchange rate. */
    convertedOverrides: number;
    /** Typed prices replaced by the new currency's price list. */
    clearedOverrides: number;
    typedPrices: TypedPricesChoice | null;
    lines: Array<{ id: string; priceSource: string; fxRateUsed: number | null }>;
};

/** Task 2.10 (consumes Task 2.3's endpoint) — switch a quote's currency and re-price every line. INTERNAL/SUPER_ADMIN only. */
export const changeQuoteCurrency = async (
    quoteId: string,
    currency: string,
    typedPrices?: TypedPricesChoice | null,
): Promise<ChangeQuoteCurrencyResult> => {
    return (await api.patch(`/quotes/${quoteId}/currency`, { currency, typedPrices: typedPrices ?? undefined })).data;
}

export type TypedPricesPreview = {
    fromCurrency: string;
    toCurrency: string;
    usdRate: number;
    typedLines: Array<{
        id: string;
        name: string;
        unitPrice: number;
        convertedUnitPrice: number;
        priceListUnitPrice: number;
    }>;
};

/** The quote's typed prices and what a switch to `currency` would do to them (no writes). */
export const previewTypedPrices = async (quoteId: string, currency: string): Promise<TypedPricesPreview> => {
    return (await api.get(`/quotes/${quoteId}/currency/typed-prices`, { params: { currency } })).data;
}
